import { getMonthBounds, getRangeBounds } from 'fitbeat.gym-core';
import moment from 'moment-timezone';

export type PulsePeriodType = 'weeks' | 'months' | 'custom';

export interface IPulsePeriodRange {
  type: PulsePeriodType;
  startDate: Date;
  endDate: Date;
}

export interface IPulsePeriodRequest {
  /** YYYY-MM when the range is exactly one calendar month. */
  month?: string;
  /** Arbitrary range (weekly or custom periods). */
  from?: Date;
  to?: Date;
}

export interface IPulsePeriodResolved {
  request: IPulsePeriodRequest;
  bounds: { monthStart: Date; monthEnd: Date; ytdStart: Date; isPartial: boolean };
  label: string;
}

function formatRangeLabel(
  start: moment.Moment,
  end: moment.Moment,
  type: PulsePeriodType,
): string {
  if (type === 'months') {
    return start.isSame(end, 'year')
      ? `${start.format('MMM')} – ${end.format('MMM YYYY')}`
      : `${start.format('MMM YYYY')} – ${end.format('MMM YYYY')}`;
  }
  if (start.isSame(end, 'month')) {
    return `${start.format('D')} – ${end.format('D MMM YYYY')}`;
  }
  return start.isSame(end, 'year')
    ? `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`
    : `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
}

/**
 * Maps a picker selection to the request, bounds and display label for a
 * Monthly Pulse run. A range covering exactly one calendar month uses the
 * legacy month request path; anything else (weekly or custom ranges) uses
 * from/to.
 */
export function resolvePulsePeriod(
  range: IPulsePeriodRange,
  timezone: string,
): IPulsePeriodResolved {
  const start = moment(range.startDate).tz(timezone);
  const end = moment(range.endDate).tz(timezone);
  // Whole month: starts exactly at the 1st 00:00 and ends on the last day of
  // the same month (getMonthBounds ends at …59.000 while presets use …59.999,
  // so compare the end at day precision).
  const isWholeMonth = start.isSame(end, 'month')
    && range.startDate.getTime() === start.clone().startOf('month').valueOf()
    && end.isSame(end.clone().endOf('month'), 'day');
  const label = isWholeMonth ? start.format('MMMM YYYY') : formatRangeLabel(start, end, range.type);
  if (isWholeMonth) {
    const month = start.format('YYYY-MM');
    return {
      request: { month },
      bounds: getMonthBounds(month, timezone),
      label,
    };
  }
  return {
    request: { from: range.startDate, to: range.endDate },
    bounds: getRangeBounds(range.startDate, range.endDate, timezone),
    label,
  };
}

/** A YYYY-MM-DD day string as start-of-day in the given timezone. */
export function startOfDayInTz(dateStr: string, timezone: string): Date {
  return moment.tz(dateStr, 'YYYY-MM-DD', timezone).startOf('day').toDate();
}

/** A YYYY-MM-DD day string as end-of-day in the given timezone. */
export function endOfDayInTz(dateStr: string, timezone: string): Date {
  return moment.tz(dateStr, 'YYYY-MM-DD', timezone).endOf('day').toDate();
}

/**
 * Client-side eligibility filter for the pulse member stats (the members
 * endpoint itself returns everyone). Mirrors the backend rules: test emails
 * contain '+test'; onboarding = long-term goal + skill level.
 */
export function filterPulseMembersForRun<
  T extends { userSettings?: { email?: string; longTermGoal?: unknown; skillLevel?: unknown } },
>(
  members: T[],
  options: { includeTestUsers: boolean; includeUnonboarded: boolean },
): T[] {
  return members.filter((member) => {
    const settings = member.userSettings;
    if (!options.includeTestUsers && settings?.email?.toLowerCase().includes('+test')) {
      return false;
    }
    if (!options.includeUnonboarded && !(settings?.longTermGoal && settings?.skillLevel)) {
      return false;
    }
    return true;
  });
}
